import React, { useState } from 'react'
import { useLocation, Link } from 'react-router-dom'
import styled from 'styled-components'
import {
  Home as HomeIconFeather,
  ArrowRight as ArrowRightIconFeather,
  Menu as MenuIconFeather,
  X as CloseIconFeather,
  Link as LinkIconFeather
} from 'react-feather'

const HomeIcon = styled(HomeIconFeather)`
  width: 20px;
  height: 20px;
`

const ArrowRightIcon = styled(ArrowRightIconFeather)`
  width: 20px;
  height: 20px;
`

const MenuIcon = styled(MenuIconFeather)`
  width: 24px;
  height: 24px;
`

const CloseIcon = styled(CloseIconFeather)`
  width: 24px;
  height: 24px;
`

const LinkIcon = styled(LinkIconFeather)`
  width: 20px;
  height: 20px;
`

// Styled components for sidebar
const SidebarContainer = styled.div`
  width: 256px;
  background-color: #000;
  padding: 1.5rem;
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  z-index: 1000;

  @media (max-width: 1440px) {
    width: 220px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`

const MobileSidebarContainer = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    padding-bottom: 3rem;
    background-color: #000;
    width: 100%;
    position: relative;
  }
`

const NavContainer = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const NavLink = styled(Link)<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  padding: 0.75rem;
  // background-color: ${({ active }) => (active ? '#333333' : 'transparent')};
  border: none;
  border-radius: 0.5rem;
  color: ${({ active }) => (active ? '#c1ff72' : '#ffffff')};
  font-size: 1rem;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;

  &:hover {
    background-color: ${({ active }) => (active ? '#333333' : '#2a2a2a')};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

const NavBlank = styled.a<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  padding: 0.75rem;
  // background-color: ${({ active }) => (active ? '#333333' : 'transparent')};
  border: none;
  border-radius: 0.5rem;
  color: ${({ active }) => (active ? '#c1ff72' : '#ffffff')};
  font-size: 1rem;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
`

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 1rem;
`

const Logo = styled.img`
  width: 100%;
  cursor: pointer;
`

const LogoMobile = styled.img`
  width: 100px;
  cursor: pointer;
`

const MobileMenuToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  outline: none;
  padding: 8px;
  border-radius: 8px;
  color: #fff;

  &:active {
    opacity: 0.8;
  }
`

const MobileMenu = styled.div<{ open: boolean }>`
  position: absolute;
  top: 64px;
  right: 0;
  background: linear-gradient(to bottom, #000000, #231f20);
  box-shadow: 0 10px 9.4px -2px rgba(0, 0, 0, 0.25);
  border-radius: 20px;
  padding: 1rem;
  display: ${({ open }) => (open ? 'flex' : 'none')};
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
  min-width: 180px;
  width: 100%;
`

const MobileMenuLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 10px 12px;
  color: #fff;
  text-decoration: none;
  border-radius: 6px;

  &:hover {
    background-color: #111;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`

const MobileMenuBlank = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 10px 12px;
  color: #fff;
  text-decoration: none;
  border-radius: 6px;

  &:hover {
    background-color: #111;
  }
`

interface SidebarProps {
  title?: string
}

export default function Sidebar({ title = 'Mova Swap' }: SidebarProps) {
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // 判断当前路由是否应该高亮Pool菜单项
  const isPoolActive =
    location.pathname === '/pool' ||
    location.pathname.startsWith('/add') ||
    location.pathname.startsWith('/remove') ||
    location.pathname.startsWith('/create') ||
    location.pathname.startsWith('/find')

  // 判断当前路由是否应该高亮Swap菜单项
  const isSwapActive = location.pathname === '/swap' || location.pathname === '/'

  const isFaucetActive = location.pathname === '/faucet'

  return (
    <>
      <MobileSidebarContainer>
        <LogoLink to="/">
          <LogoMobile src={'/images/logo.svg'} alt="Mova Swap" />
        </LogoLink>
        <MobileMenuToggle onClick={() => setMobileMenuOpen(o => !o)} aria-label="Open menu">
          {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </MobileMenuToggle>
        <MobileMenu open={mobileMenuOpen}>
          <MobileMenuBlank href="https://www.movachain.com/" target="_blank" onClick={() => setMobileMenuOpen(false)}>
            <HomeIcon />
            Home
          </MobileMenuBlank>
          <MobileMenuLink to="/faucet" onClick={() => setMobileMenuOpen(false)}>
            <ArrowRightIcon />
            Mova Faucet
          </MobileMenuLink>
          <MobileMenuLink to="/" onClick={() => setMobileMenuOpen(false)}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
            Mova Swap
          </MobileMenuLink>
          <MobileMenuLink to="/pool" onClick={() => setMobileMenuOpen(false)}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            Mova Pool
          </MobileMenuLink>
          <MobileMenuBlank
            href="https://bridge-fmrs88g7.movachain.com"
            target="_blank"
            onClick={() => setMobileMenuOpen(false)}
          >
            <LinkIcon />
            Mova Bridge
          </MobileMenuBlank>
        </MobileMenu>
      </MobileSidebarContainer>
      <SidebarContainer>
        <LogoLink to="/">
          <Logo src={'/images/logo.svg'} alt="Mova Swap" />
        </LogoLink>
        <NavContainer>
          {/* Home */}
          <NavBlank href="https://www.movachain.com/" target="_blank">
            <HomeIcon />
            <span>Home</span>
          </NavBlank>
          {/* Mova Faucet */}
          <NavLink to="/faucet" active={isFaucetActive}>
            <ArrowRightIcon />
            Mova Faucet
          </NavLink>
          <NavLink to="/" active={isSwapActive}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
            <span>Mova Swap</span>
          </NavLink>
          <NavLink to="/pool" active={isPoolActive}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <span>Mova Pool</span>
          </NavLink>
          {/* Mova Bridge */}
          <NavBlank href="https://bridge-fmrs88g7.movachain.com" target="_blank">
            <LinkIcon />
            <span>Mova Bridge</span>
          </NavBlank>
        </NavContainer>
      </SidebarContainer>
    </>
  )
}
